<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12">
        <subtitle-component>
          {{ $t("message.visibility.summary.websitesTitle") }}
        </subtitle-component>
        <v-card>
          <v-row no-gutters class="visibility-container">
            <v-col v-if="!loading" class="pa-8">
              <template v-if="!websites.length > 0">
                <NoDataInfo
                  :title="$t('message.visibility.summary.noWebsiteAddress')"
                  :description="
                    $t('message.visibility.summary.noWebsiteAddressInfo')
                  "
                  :buttonName="$t('message.common.newWebsite')"
                  buttonRouteName="profile-websites"
                />
              </template>

              <template v-if="websites && websites.length > 0">
                <v-row>
                  <v-col cols="12">
                    <p class="summary__subtitle">
                      {{ $t("message.visibility.summary.websites.subtitle") }}
                    </p>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col md="8">
                    <SelectBar
                      :items="websites"
                      :selectedItem="selectedWebsite"
                      :inputPlaceholder="$t('message.common.website')"
                      :buttonName="$t('message.common.newWebsite')"
                      buttonSlug="add_website"
                      @selected="getLocationsForSelectedWebsite"
                      buttonRouteName="profile-websites"
                    >
                      <SelectWebsite
                        slot="selectedItem"
                        slot-scope="data"
                        :data="data"
                      />
                      <SelectWebsite
                        slot="allItems"
                        slot-scope="data"
                        :data="data"
                      />
                    </SelectBar>
                    <SelectBar
                      :searchActive="true"
                      :items="selectedWebsite.search_locations"
                      :selectedItem="selectedLocation"
                      :inputPlaceholder="$t('message.common.location')"
                      :buttonName="$t('message.common.newLocation')"
                      buttonSlug="add_location"
                      @selected="getKeywordsForSelectedLocation"
                      buttonRouteName="profile-company-in-web"
                    >
                      <SelectLocation
                        slot="selectedItem"
                        slot-scope="data"
                        :data="data"
                      />
                      <SelectLocation
                        slot="allItems"
                        slot-scope="data"
                        :data="data"
                      />
                    </SelectBar>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="8">
                    <div class="p-relative">
                      <template
                        v-if="
                          selectedKeywordDataObj.data.length > 0 &&
                            selectedKeywordDataObj.labels.length > 0
                        "
                      >
                        <gradient-line-chart
                          :width="370"
                          :height="500"
                          :data="selectedKeywordDataObj.data"
                          :labels="selectedKeywordDataObj.labels"
                          :loading="loadingChart"
                          @loadingChartStatus="loadingChart = $event.value"
                        ></gradient-line-chart>
                      </template>
                      <template v-else>
                        <v-col
                          cols="12"
                          class="d-flex justify-center fill-height flex-column align-center"
                          style="height: 500px;"
                        >
                          <v-icon x-large color="primary"
                            >mdi-database-search</v-icon
                          >
                          <h3 class="mb-3 w-100">
                            {{ $t("message.common.noData") }}
                          </h3>
                          <p>
                            {{
                              $t(
                                "message.visibility.summary.dataCollectionForKeywordTryAgainLater"
                              )
                            }}
                          </p>
                        </v-col>
                      </template>
                      <ChartPagination
                        @changeMonth="changeMonth"
                        :currentMonth="currentMonth"
                      />
                      <SectionLoader :status="loadingChart" />
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <template
                      v-if="
                        selectedLocationKeywords.length > 0 &&
                          selectedKeywordId !== null
                      "
                    >
                      <KeywordList
                        :items="selectedLocationKeywords"
                        :loading="loadingKeywordList"
                        :selectedId.sync="selectedKeywordId"
                        :selectedAdditionalItemId="selectedWebsite.id"
                        @update:selectedId="getKeywordDataOnClick"
                        @loadingKeywords="loadingKeywordList = $event.value"
                      />
                    </template>
                    <template v-else>
                      <v-col cols="12">
                        <h4 class="mb-3">
                          {{ $t("message.common.noData") }}
                        </h4>
                        <p>
                          {{ $t("message.visibility.summary.addKeywords") }}
                        </p>
                        <v-btn color="primary" class="mb-0 mt-4">
                          <v-icon small>icon-plus</v-icon
                          >{{ $t("message.common.addNewTerm") }}
                        </v-btn>
                      </v-col>
                    </template>
                  </v-col>
                </v-row>
              </template>
            </v-col>
            <SectionLoader :status="loading" />
          </v-row>
        </v-card>
      </v-col>

      <SocialMediaAudit id="social-media" />
    </v-row>
  </v-container>
</template>

<script>
import SocialMediaAudit from "@/components/hoc/Visibility/SocialMediaAudit.vue";
import KeywordList from "@/components/Visibility/KeywordList.vue";
import NoDataInfo from "@/components/Visibility/NoDataInfo.vue";
import SelectBar from "@/components/Visibility/SelectBar.vue";
import SelectWebsite from "@/components/Visibility/SelectBar/Websites.vue";
import SelectLocation from "@/components/Visibility/SelectBar/Locations.vue";
import GradientLineChart from "@/components/Visibility/Websites/Chart/chart.js";
import ChartPagination from "@/components/Chart/Pagination.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import SubtitleComponent from "@/components/ui/Subtitle/Subtitle.component.vue";

import websitesService from "@/services/websites";
import serviceHandler from "@/helpers/handlers/service";

import moment from "moment";

import { ChartConfig } from "@/config/chart.js";

export default {
  name: "VisibilityWebsites",
  components: {
    SocialMediaAudit,
    KeywordList,
    NoDataInfo,
    SelectBar,
    SelectWebsite,
    SelectLocation,
    GradientLineChart,
    SectionLoader,
    ChartPagination,
    SubtitleComponent
  },
  data() {
    return {
      ChartConfig,
      loading: false,
      loadingKeywordList: false,
      loadingChart: false,
      websites: [],
      selectedWebsite: {},
      selectedLocation: {},
      selectedLocationKeywords: [],
      selectedKeywordId: null,
      selectedKeywordData: [],
      currentMonth: {}
    };
  },
  async created() {
    this.loading = true;
    await this.getCurrentMonth();
    await this.fetchWebsites();
    if (this.websites.length > 0) {
      await this.selectWebsite();
      await this.selectLocation();
      await this.fetchLocationKeywords();
      await this.getKeywordData(this.selectedLocationKeywords[0].id);
      this.selectedKeywordId = this.selectedLocationKeywords[0].id;
    }
    await this.$store.commit("websites/SET_LOADING", false);
    this.loading = false;
  },

  computed: {
    selectedKeywordDataObj() {
      if (this.selectedKeywordData.length > 0) {
        return {
          labels: this.selectedKeywordData.map(item => item.searched_at),
          data: this.selectedKeywordData.map(item => item.position_organic)
        };
      }
      return {
        labels: [],
        data: []
      };
    }
  },

  methods: {
    async fetchWebsites() {
      await this.getCurrentMonth();

      const data = await serviceHandler(websitesService.getWebsitesData());

      if (data.length > 0) {
        this.websites = data;
      }
    },

    selectWebsite(id) {
      const website = !id
        ? this.websites[0]
        : this.websites.find(website => website.id === id);

      this.selectedWebsite = website;
    },

    selectLocation(id) {
      const location = !id
        ? this.selectedWebsite.search_locations[0]
        : this.selectedWebsite.search_locations.find(
            location => location.id === id
          );

      this.selectedLocation = location;
    },

    async fetchLocationKeywords() {
      const websiteId = this.selectedWebsite.id;
      const locationId = this.selectedLocation.id;
      const data = await serviceHandler(
        websitesService.getKeywordsByLocation(websiteId, locationId)
      );

      if (data.length > 0) {
        const selected = data.find(
          item => item.custom_search_location_id === this.selectedLocation.id
        );

        this.selectedLocationKeywords = selected.keywords;
      }
    },

    getCurrentMonth(month) {
      month = month || moment();
      this.currentMonth = month;
    },

    getLocationsForSelectedWebsite(value) {
      this.selectWebsite(value.id);
      this.getKeywordsForSelectedLocation(this.selectedLocation.id);
    },

    getKeywordsForSelectedLocation(value) {
      this.loadingKeywordList = true;
      this.selectLocation(value.id);
      this.fetchLocationKeywords();
      this.getKeywordData(this.selectedLocationKeywords[0].id);
      this.loadingKeywordList = false;
    },

    getKeywordDataOnClick(keywordId) {
      this.getKeywordData(keywordId);
    },

    async getKeywordData(keywordId) {
      this.loadingChart = true;
      const websiteId = this.selectedWebsite.id;
      const locationId = this.selectedLocation.id;
      this.selectedKeywordId = keywordId;

      this.selectedKeywordData = await serviceHandler(
        websitesService.getKeywordData(
          {
            websiteId,
            locationId,
            keywordId: keywordId
          },
          {
            from: moment(this.currentMonth)
              .startOf("month")
              .format("YYYY-MM-DD"),
            to: moment(this.currentMonth)
              .endOf("month")
              .startOf("day")
              .format("YYYY-MM-DD")
          }
        )
      );

      this.loadingChart = false;
    },

    changeMonth(val) {
      this.getCurrentMonth(val);

      this.getKeywordData(this.selectedKeywordId);
    }
  }
};
</script>

<style lang="scss" scoped>
.visibility-container {
  min-height: 800px;
  position: relative;
}

.summary__subtitle {
  text-align: center;
  font-weight: $font-weight-semi-bold;
  margin-bottom: 0;
}
</style>
